import RequirePermissions from '@/components/auth/requirePermissions';
import { AiAssistantButton } from '@/components/GenAI/AiAssistantButton';
import { AiAssistantDrawer } from '@/components/GenAI/AiAssistantDrawer';
import { useArePrecedingMilestonesCompleted } from '@/hooks/useArePrecedingMilestonesCompleted';
import { useGetDrawerMarginStyles } from '@/hooks/useGetDrawerMarginStyles';
import { useParentDivLayoutStyles } from '@/hooks/useParentDivLayoutStyles';
import { Alert, Box, useOpenClose } from '@palmetto/palmetto-components';
import { useEffect, useMemo, useRef } from 'react';
import { Helmet } from 'react-helmet';
import { useNavigate, useParams } from 'react-router';
import { Link } from 'react-router-dom';

import { MilestoneType, QueueType, UserPermissions } from 'types';

import { useGetAccountQuery } from '../../services/accounts';
import { useGetQueuesByAccountIdQuery } from '../../services/queues';
import { useGetTasksQuery } from '../../services/tasks';
import { ActivationPackageActions } from '../../types/ActivationPackage';
import { taskToMilestoneMap } from '../../types/Tasks';
import { AccountStatusIndicators } from '../Account/AccountStatusIndicators';
import RequirePalmettoFinanceUser from '../auth/RequirePalmettoFinanceUser';
import FormSection from '../Forms/FormSection';
import { MainContainer } from '../MainContainer';
import PageHeader from '../PageHeader';
import { SkeletonBox } from '../SkeletonBox';
import AdminStipulationBannerList from '../Stipulations/AdminStipulationBannerList';

import { ActivationMilestonePackageFactory } from './ActivationMilestonePackageFactory';

export const ActivationMilestonePackage = () => {
  const { id, action } = useParams<{ id: any; action: ActivationPackageActions }>();
  const navigate = useNavigate();
  const { data: account, isLoading: accountIsLoading } = useGetAccountQuery(id);
  const { data: items = [], isLoading: tasksAreLoading } = useGetTasksQuery({ id });
  const { data: activationQueues } = useGetQueuesByAccountIdQuery({
    accountId: id,
    types: [QueueType.activationPackage],
  });
  const activationQueue = activationQueues?.[0];
  const targetRef = useRef(null);

  const legacyReviewTasks = useMemo(() => {
    return items.filter(
      (item) => item.assigneeType === 'internal' && taskToMilestoneMap[item.type] === MilestoneType.systemActivation,
    );
  }, [items]);

  const canSubmitActivationPackage = useArePrecedingMilestonesCompleted(
    account?.milestones,
    MilestoneType.activation,
  );

  useEffect(() => {
    if (!action || !ActivationPackageActions[action]) {
      navigate(`/accounts/${id}`, { replace: true });
    }
  }, [id, navigate, action]);

  const {
    isOpen: isModelOutputsDrawerOpen,
    handleClose: closeModelOutputsDrawer,
    handleToggle: toggleModelOutputsDrawer,
  } = useOpenClose();
  const parentStyles = useParentDivLayoutStyles(targetRef);
  const drawerMarginStyles = useGetDrawerMarginStyles(parentStyles, isModelOutputsDrawerOpen, 'left');

  return (
    <>
      <Helmet>
        <title>{`${account?.primaryApplicantName} Activation Package`}</title>
      </Helmet>
      <MainContainer>
        <Box
          ref={targetRef}
          style={{
            marginLeft: drawerMarginStyles,
            transition: 'margin-left 0.3s ease',
          }}
          gap="md"
        >
          <AdminStipulationBannerList />
          <PageHeader
            eyebrow={
              <>
                <Link to="/accounts">Accounts</Link> \{' '}
                <Link to={`/accounts/${id}`}>{`${account?.primaryApplicantName}`}</Link>
              </>
            }
            title="Activation Package"
            description="Provide the required documents and monitoring information."
          />
          <AccountStatusIndicators account={account} />
          {account && action !== ActivationPackageActions.review && !canSubmitActivationPackage && (
            <Alert
              variant="warning"
              hasIcon
              title="Install Package is not complete"
              message="The Install Package must be completed and approved before submitting the Activation Package."
            />
          )}
          {accountIsLoading || tasksAreLoading ? (
            <FormSection title="Loading...">
              <SkeletonBox height="25px" width="100" />
            </FormSection>
          ) : (
            <ActivationMilestonePackageFactory
              legacyReviewTasks={legacyReviewTasks}
              canSubmitActivationPackage={canSubmitActivationPackage}
            />
          )}
        </Box>
        <RequirePalmettoFinanceUser>
          <RequirePermissions permissions={[UserPermissions.lightReachQCTech]}>
            <AiAssistantButton
              toggleDrawer={toggleModelOutputsDrawer}
              accountId={id}
              isDrawerOpen={isModelOutputsDrawerOpen}
            />
            <AiAssistantDrawer
              accountId={id}
              queueId={activationQueue?.id}
              isOpen={isModelOutputsDrawerOpen}
              onClose={closeModelOutputsDrawer}
            />
          </RequirePermissions>
        </RequirePalmettoFinanceUser>
      </MainContainer>
    </>
  );
};
